<template>
    <v-dialog v-model="internalDialog" max-width="700" persistent>
        <v-card>
            <v-card-title class="text-h5">
                Edit Milestone
                <v-spacer></v-spacer>
                <v-btn icon @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-subtitle class="pb-0">
                Update milestone details and track progress.
            </v-card-subtitle>

            <v-card-text>
                <v-form ref="form" v-model="valid">
                    <!-- Milestone Name -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Milestone Name <span class="red--text">*</span>
                            <v-tooltip right>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon small color="grey" v-bind="attrs" v-on="on" class="ml-1">
                                        mdi-information-outline
                                    </v-icon>
                                </template>
                                <span>Enter a clear, descriptive name for this milestone</span>
                            </v-tooltip>
                        </label>
                        <v-text-field 
                            v-model="formData.name" 
                            placeholder="Enter milestone name" 
                            outlined 
                            dense
                            :rules="[rules.required, rules.maxLength]"
                        ></v-text-field>
                    </div>

                    <!-- Date Row -->
                    <v-row>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Start Date <span class="red--text">*</span>
                                    <v-tooltip right>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small color="grey" v-bind="attrs" v-on="on" class="ml-1">
                                                mdi-information-outline
                                            </v-icon>
                                        </template>
                                        <span>When work on this milestone begins</span>
                                    </v-tooltip>
                                </label>
                                <v-menu
                                    v-model="startDateMenu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="formattedStartDate"
                                            placeholder="January 26th, 2026"
                                            prepend-inner-icon="mdi-calendar"
                                            readonly
                                            outlined
                                            dense
                                            v-bind="attrs"
                                            v-on="on"
                                            clearable
                                            @click:clear="formData.start_date = null"
                                            :rules="[rules.required]"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="formData.start_date"
                                        @input="startDateMenu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </div>
                        </v-col>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Due Date <span class="red--text">*</span>
                                    <v-tooltip right>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small color="grey" v-bind="attrs" v-on="on" class="ml-1">
                                                mdi-information-outline
                                            </v-icon>
                                        </template>
                                        <span>Target completion date for this milestone</span>
                                    </v-tooltip>
                                </label>
                                <v-menu
                                    v-model="dueDateMenu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="formattedDueDate"
                                            placeholder="Pick a date"
                                            prepend-inner-icon="mdi-calendar"
                                            readonly
                                            outlined
                                            dense
                                            v-bind="attrs"
                                            v-on="on"
                                            clearable
                                            @click:clear="formData.due_date = null"
                                            :rules="[rules.required]"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="formData.due_date"
                                        @input="dueDateMenu = false"
                                        :min="formData.start_date"
                                    ></v-date-picker>
                                </v-menu>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Status and Assigned To Row -->
                    <v-row>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Status
                                    <v-tooltip right>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small color="grey" v-bind="attrs" v-on="on" class="ml-1">
                                                mdi-information-outline
                                            </v-icon>
                                        </template>
                                        <span>Current status of the milestone</span>
                                    </v-tooltip>
                                </label>
                                <v-select 
                                    v-model="formData.status" 
                                    :items="statusOptions" 
                                    placeholder="Pending" 
                                    outlined
                                    dense
                                ></v-select>
                            </div>
                        </v-col>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Assigned To <span class="red--text">*</span>
                                    <v-tooltip right>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small color="grey" v-bind="attrs" v-on="on" class="ml-1">
                                                mdi-information-outline
                                            </v-icon>
                                        </template>
                                        <span>Person responsible for completing this milestone</span>
                                    </v-tooltip>
                                </label>
                                <v-select 
                                    v-model="formData.assigned_to" 
                                    :items="userOptions" 
                                    item-text="name"
                                    item-value="id"
                                    placeholder="Select person" 
                                    outlined
                                    dense
                                    :rules="[rules.required]"
                                >
                                    <template v-slot:selection="{ item }">
                                        {{ item.name }}
                                    </template>
                                    <template v-slot:item="{ item }">
                                        <v-list-item-avatar size="32">
                                            <v-img v-if="item.avatar" :src="item.avatar"></v-img>
                                            <v-avatar v-else color="primary">
                                                <span class="white--text text-caption">{{ getInitials(item.name) }}</span>
                                            </v-avatar>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                                            <v-list-item-subtitle v-if="item.position_name">
                                                {{ item.position_name }}
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </template>
                                </v-select>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Completion Percentage -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-3 d-block">
                            Completion Percentage
                            <v-tooltip right>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon small color="grey" v-bind="attrs" v-on="on" class="ml-1">
                                        mdi-information-outline
                                    </v-icon>
                                </template>
                                <span>Current progress towards completing this milestone</span>
                            </v-tooltip>
                        </label>
                        <div class="d-flex align-center">
                            <v-slider
                                v-model="formData.completion_percentage"
                                :thumb-size="24"
                                thumb-label="always"
                                min="0"
                                max="100"
                                step="5"
                                class="flex-grow-1 mr-4"
                            >
                                <template v-slot:thumb-label="{ value }">
                                    {{ value }}%
                                </template>
                            </v-slider>
                            <div class="text-h6 font-weight-bold" style="min-width: 50px;">
                                {{ formData.completion_percentage }}%
                            </div>
                        </div>
                    </div>

                    <!-- Notes -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Notes
                            <v-tooltip right>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon small color="grey" v-bind="attrs" v-on="on" class="ml-1">
                                        mdi-information-outline
                                    </v-icon>
                                </template>
                                <span>Additional information or context about this milestone</span>
                            </v-tooltip>
                        </label>
                        <v-textarea 
                            v-model="formData.notes"
                            placeholder="Add notes about this milestone..." 
                            outlined 
                            dense 
                            rows="3"
                            auto-grow
                        ></v-textarea>
                    </div>

                    <!-- Mark as Completed Checkbox -->
                    <div class="mb-2">
                        <v-checkbox
                            v-model="markAsCompleted"
                            label="Mark as completed"
                            dense
                            hide-details
                            @change="handleCompletedChange"
                        ></v-checkbox>
                    </div>
                </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-6">
                <v-btn text color="red" @click="confirmDelete" :disabled="loading">
                    <v-icon left>mdi-delete</v-icon>
                    Delete
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog">
                    Cancel
                </v-btn>
                <v-btn 
                    color="primary" 
                    @click="updateMilestone" 
                    :loading="loading"
                    :disabled="!valid"
                >
                    Update Milestone
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="500px">
            <v-card>
                <v-card-title class="headline red white--text">
                    <v-icon left color="white">mdi-delete</v-icon>
                    Delete Milestone
                </v-card-title>
                <v-card-text class="pa-6">
                    <p class="text-body-1">Are you sure you want to delete this milestone?</p>
                    <p class="text-subtitle-1 font-weight-bold mt-2">
                        {{ milestone ? milestone.name : '' }}
                    </p>
                    <p class="text-caption grey--text mt-2">
                        This action cannot be undone.
                    </p>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-spacer></v-spacer>
                    <v-btn text @click="deleteDialog = false">
                        Cancel
                    </v-btn>
                    <v-btn color="red" dark @click="deleteMilestone" :loading="deleteLoading">
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
export default {
    name: 'EditMilestoneDialog',
    props: {
        value: Boolean,
        milestone: {
            type: Object,
            default: null
        },
        users: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            valid: false,
            loading: false,
            deleteLoading: false,
            deleteDialog: false,
            startDateMenu: false,
            dueDateMenu: false,
            markAsCompleted: false,
            formData: {
                name: '',
                start_date: null,
                due_date: null,
                status: 'Pending',
                assigned_to: null,
                completion_percentage: 0,
                notes: ''
            },
            statusOptions: [
                'Pending',
                'In Progress',
                'Review',
                'Blocked',
                'Completed',
                'Cancelled'
            ],
            rules: {
                required: v => !!v || 'This field is required',
                maxLength: v => !v || v.length <= 255 || 'Maximum 255 characters allowed'
            }
        };
    },
    computed: {
        internalDialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        userOptions() {
            return this.users;
        },
        formattedStartDate() {
            if (!this.formData.start_date) return '';
            return this.$date(this.formData.start_date).format('MMMM Do, YYYY');
        },
        formattedDueDate() {
            if (!this.formData.due_date) return '';
            return this.$date(this.formData.due_date).format('MMMM Do, YYYY');
        }
    },
    watch: {
        value(newVal) {
            if (newVal && this.milestone) {
                this.loadMilestoneData();
            }
        },
        milestone(newVal) {
            if (newVal && this.value) {
                this.loadMilestoneData();
            }
        }
    },
    methods: {
        getInitials(name) {
            if (!name) return '';
            return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
        },
        loadMilestoneData() {
            if (!this.milestone) return;
            
            this.formData = {
                name: this.milestone.name || '',
                start_date: this.milestone.start_date || null,
                due_date: this.milestone.due_date || null,
                status: this.milestone.status || 'Pending',
                assigned_to: this.milestone.assigned_to || null,
                completion_percentage: this.milestone.completion_percentage || 0,
                notes: this.milestone.notes || ''
            };
            this.markAsCompleted = this.milestone.status === 'Completed';
            
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        handleCompletedChange(value) {
            if (value) {
                this.formData.status = 'Completed';
                this.formData.completion_percentage = 100;
            } else {
                this.formData.status = 'Pending';
            }
        },
        closeDialog() {
            this.internalDialog = false;
            this.deleteDialog = false;
        },
        confirmDelete() {
            this.deleteDialog = true;
        },
        async deleteMilestone() {
            if (!this.milestone || !this.milestone.id) return;
            
            this.deleteLoading = true;
            try {
                const response = await this.$axios.delete(`api/milestones/${this.milestone.id}`);

                if (response.data.response) {
                    this.$toast.success('Milestone deleted successfully');
                    this.$emit('milestone-deleted', this.milestone.id);
                    this.deleteDialog = false;
                    this.closeDialog();
                } else {
                    this.$toast.error('Failed to delete milestone');
                }
            } catch (error) {
                console.error('Error deleting milestone:', error);
                this.$toast.error(error.response?.data?.message || 'Failed to delete milestone');
            } finally {
                this.deleteLoading = false;
            }
        },
        async updateMilestone() {
            if (!this.$refs.form.validate()) {
                return;
            }

            if (!this.milestone || !this.milestone.id) {
                this.$toast.error('Invalid milestone');
                return;
            }

            this.loading = true;
            try {
                // Update milestone data according to backend API requirements
                const milestoneData = {
                    name: this.formData.name,
                    start_date: this.formData.start_date,
                    due_date: this.formData.due_date,
                    status: this.formData.status,
                    assigned_to: this.formData.assigned_to,
                    completion_percentage: this.formData.completion_percentage,
                    notes: this.formData.notes,
                    is_completed: this.markAsCompleted
                };

                // Update milestone via API
                const response = await this.$axios.put(`api/milestones/${this.milestone.id}`, milestoneData);

                if (response.data.response) {
                    this.$toast.success('Milestone updated successfully');
                    this.$emit('milestone-updated', response.data.data);
                    this.closeDialog();
                } else {
                    this.$toast.error('Failed to update milestone');
                }
            } catch (error) {
                console.error('Error updating milestone:', error);
                this.$toast.error(error.response?.data?.message || 'Failed to update milestone');
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.v-card__title {
    background-color: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
}

.v-card__subtitle {
    color: #666;
}

label {
    color: #333;
}

.v-slider {
    margin-top: 12px;
}
</style>
